/**
 * Menu Configuration
 * Defines all possible menu items with their structure
 * This is safe to expose in frontend as it contains no sensitive data
 */

import {
	LayoutDashboard,
	Users,
	UserPlus,
	Eye,
	BarChart3,
	FileSpreadsheet,
	TrendingUp,
	Server,
	Link,
	FileText,
	Database,
	HelpCircle,
	AlertCircle,
	Building2,
	UserX,
	Menu,
	Truck,
	MapPin,
	Navigation,
	Package,
	ArrowRight,
	Briefcase,
	Activity,
	ClipboardList,
	PhoneCall,
	BarChart,
	ShoppingCart,
	Box,
	Receipt,
	DollarSign,
	UserCog,
	Building,
	BadgeDollarSign,
	Award,
	Calendar,
	ShieldCheck,
	Inbox,
	Zap,
	Wallet,
	ArrowDownCircle,
	ArrowUpCircle,
	KanbanSquare,
	// ArrowLeft, // Deprecated - Received section commented out
} from 'lucide-react';

import { MenuItem } from '../types/menu';

// All possible menu items (safe to expose)
export const ALL_MENU_ITEMS: MenuItem[] = [
	// 1. DASHBOARDS (Most accessed)
	{
		id: 'dashboard',
		name: 'Dashboards',
		icon: LayoutDashboard,
		children: [
			{
				id: 'impact-home',
				name: 'Impact Home',
				icon: LayoutDashboard,
				href: '/',
			},
			{
				id: 'ops-dashboard',
				name: 'Ops Dashboard',
				icon: Activity,
				href: '/ops-dashboard',
			},
			{
				id: '3d-builder',
				name: '3D Builder',
				icon: Box,
				href: '/dashboard/3d-builder',
			},
		],
	},
	// 2. CORE OPERATIONS (Daily work)
	{
		id: 'transit-plan',
		name: 'Transit Plan',
		icon: Truck,
		children: [
			{
				id: 'master-plan',
				name: 'Master Plan',
				icon: MapPin,
				children: [
					{
						id: 'create-transit-master-plan',
						name: 'Create Plan',
						icon: MapPin,
						href: '/transit-plan/master-plan/create',
					},
					{
						id: 'master-plan-listing',
						name: 'Master Plan Listing',
						icon: FileText,
						href: '/transit-plan/master-plan/listing',
					},
				],
			},
			{
				id: 'transit-plan-listing',
				name: 'Transit Plan Listing',
				icon: FileText,
				href: '/transit-plan/listing',
			},
			{
				id: 'route-optimizer',
				name: 'Route Optimizer',
				icon: Navigation,
				href: '/transit-plan/route-optimizer',
			},
			{
				id: 'sent-inventory',
				name: 'Sent Inventory',
				icon: ArrowRight,
				children: [
					{
						id: 'sent-transit-plan',
						name: 'Sent Transit Plan',
						icon: ArrowRight,
						href: '/transit-plan/sent/plan',
					},
					{
						id: 'sent-inventory-listing',
						name: 'Sent Inventory Listing',
						icon: Package,
						href: '/transit-plan/sent/inventory',
					},
				],
			},
			// TODO: Received section is deprecated - information will be captured elsewhere
			// {
			// 	id: 'received-inventory',
			// 	name: 'Received Inventory',
			// 	icon: ArrowLeft,
			// 	children: [
			// 		{
			// 			id: 'received-transit-plan',
			// 			name: 'Received Transit Plan',
			// 			icon: ArrowLeft,
			// 			href: '/transit-plan/received/plan',
			// 		},
			// 		{
			// 			id: 'received-inventory-listing',
			// 			name: 'Received Inventory Listing',
			// 			icon: Package,
			// 			href: '/transit-plan/received/listing',
			// 		},
			// 	],
			// },
		],
	},
	{
		id: 'operations-reporting',
		name: 'Operations Reporting',
		icon: ClipboardList,
		children: [
			{
				id: 'shift-reporting',
				name: 'Shift Reporting',
				icon: FileText,
				children: [
					{
						id: 'shift-reporting-add',
						name: 'New Shift Report',
						icon: UserPlus,
						href: '/operations-reporting/shift-reporting/add',
					},
					{
						id: 'shift-reporting-listing',
						name: 'Shift Report Listing',
						icon: FileText,
						href: '/operations-reporting/shift-reporting/listing',
					},
				],
			},
			{
				id: 'qc-rejection',
				name: 'QC Rejection',
				icon: FileText,
				children: [
					{
						id: 'qc-rejection-add',
						name: 'New QC Rejection',
						icon: UserPlus,
						href: '/operations-reporting/qc-rejection/add',
					},
					{
						id: 'qc-rejection-listing',
						name: 'QC Rejection Listing',
						icon: FileText,
						href: '/operations-reporting/qc-rejection/listing',
					},
				],
			},
			{
				id: 'client-escalation',
				name: 'Escalation',
				icon: AlertCircle,
				children: [
					{
						id: 'client-escalation-listing',
						name: 'Client Escalation Listing',
						icon: FileText,
						href: '/operations-reporting/client-escalation/listing',
					},
				],
			},
		],
	},
	{
		id: 'kam',
		name: 'KAM',
		icon: Briefcase,
		children: [
			{
				id: 'kam-clients',
				name: 'Client List',
				icon: Users,
				href: '/kam/clients',
			},
			{
				id: 'kam-inventory',
				name: 'Client Inventory',
				icon: Package,
				href: '/kam/inventory',
			},
		],
	},
	// 3. FINANCIAL (Grouped together)
	{
		id: 'revenue',
		name: 'Revenue',
		icon: TrendingUp,
		children: [
			{
				id: 'monthly-estimate',
				name: 'Monthly Estimate',
				icon: BarChart3,
				children: [
					{
						id: 'monthly-estimate-add',
						name: 'New Monthly Estimate',
						icon: UserPlus,
						href: '/revenue/monthly-estimate/add',
					},
					{
						id: 'monthly-estimate-list',
						name: 'Monthly Estimate Listing',
						icon: FileText,
						href: '/revenue/monthly-estimate/list',
					},
				],
			},
		],
	},
	{
		id: 'p-and-l',
		name: 'P&L Review',
		icon: BarChart3,
		children: [
			{
				id: 'city-kpi',
				name: 'My City KPIs',
				icon: BarChart3,
				href: '/my-city/kpi',
			},
			{
				id: 'pl-summary',
				name: 'P&L Summary',
				icon: FileText,
				href: '/p-and-l/summary',
			},
			{
				id: 'pl-graphs',
				name: 'P&L Graphs',
				icon: BarChart3,
				href: '/p-and-l/graphs',
			},
			{
				id: 'pl-location-variance',
				name: 'Location Variance',
				icon: BarChart3,
				href: '/p-and-l/location-variance',
			},
		],
	},
	{
		id: 'manpower',
		name: 'Manpower P&L',
		icon: BarChart3,
		children: [
			{
				id: 'manpower-vendors',
				name: 'Vendors',
				icon: FileText,
				href: '/manpower/vendors',
			},
			{
				id: 'manpower-workers',
				name: 'Workers',
				icon: FileText,
				href: '/manpower/workers',
			},
			{
				id: 'manpower-attendance',
				name: 'Attendance',
				icon: Calendar,
				href: '/manpower/attendance',
			},
			{
				id: 'manpower-vehicles',
				name: 'Vehicles',
				icon: Truck,
				href: '/manpower/vehicles',
			},
			{
				id: 'manpower-cost',
				name: 'Cost',
				icon: BarChart3,
				href: '/manpower/cost',
			},
		],
	},
	{
		id: 'logistics',
		name: 'Logistics P&L',
		icon: Truck,
		children: [
			{
				id: 'logistics-vehicle-rates',
				name: 'Vehicle Rates',
				icon: Truck,
				href: '/logistics/vehicle-rates',
			},
			{
				id: 'logistics-bookings',
				name: 'Ad-hoc Bookings',
				icon: FileText,
				href: '/logistics/bookings',
			},
			{
				id: 'logistics-cost',
				name: 'Logistics Cost',
				icon: BarChart3,
				href: '/logistics/cost',
			},
		],
	},
	{
		id: 'test-evidence',
		name: 'Test Evidence',
		icon: ShieldCheck,
		href: '/test-evidence',
	},
	{
		id: 'finances',
		name: 'Finances',
		icon: DollarSign,
		children: [
			{
				id: 'amazon-invoice',
				name: 'Amazon Invoice',
				icon: Receipt,
				children: [
					{
						id: 'amazon-invoice-listing',
						name: 'Amazon Invoice Listing',
						icon: Receipt,
						href: '/finances/amazon-invoice',
					},
					{
						id: 'amazon-invoice-upload',
						name: 'Amazon Invoice Upload',
						icon: Receipt,
						href: '/finances/amazon-invoice/upload',
					},
				],
			},
			{
				id: 'zoho-inflow',
				name: 'Zoho Inflow (AR)',
				icon: ArrowDownCircle,
				children: [
					{
						id: 'zoho-invoices',
						name: 'Invoices',
						icon: FileText,
						href: '/finances/zoho-invoices',
					},
					{
						id: 'zoho-payment-received',
						name: 'Payments Received',
						icon: Receipt,
						href: '/finances/zoho-payment-received',
					},
				],
			},
			{
				id: 'zoho-outflow',
				name: 'Zoho Outflow (AP)',
				icon: ArrowUpCircle,
				children: [
					{
						id: 'zoho-bills',
						name: 'Bills',
						icon: FileText,
						href: '/finances/zoho-bills',
					},
					{
						id: 'zoho-vendor-payments',
						name: 'Payments Made',
						icon: Wallet,
						href: '/finances/zoho-vendor-payments',
					},
					{
						id: 'zoho-expenses',
						name: 'Expenses',
						icon: Receipt,
						href: '/finances/zoho-expenses',
					},
				],
			},
		],
	},
	// 3b. CASHFLOW (payment reconciliation intelligence)
	{
		id: 'cashflow',
		name: 'Cashflow',
		icon: Wallet,
		children: [
			{
				id: 'cashflow-dashboard',
				name: 'Cash Flow',
				icon: Activity,
				href: '/cashflow/dashboard',
			},
			{
				id: 'cashflow-cockpit',
				name: 'Cockpit',
				icon: Activity,
				href: '/cashflow/cockpit',
			},
			{
				id: 'cashflow-reconciliation',
				name: 'Reconciliation',
				icon: ShieldCheck,
				href: '/cashflow/reconciliation',
			},
		],
	},
	// 3c. PAYROLL (salary sheets per site and wage month)
	{
		id: 'payroll',
		name: 'Payroll',
		icon: BadgeDollarSign,
		children: [
			{ id: 'payroll-overview', name: 'Overview', icon: BarChart3, href: '/payroll' },
			{ id: 'payroll-entry', name: 'Salary Sheets', icon: FileSpreadsheet, href: '/payroll/entry' },
		],
	},
	// 4. ANALYTICS & REPORTS (Combined)
	{
		id: 'analytics',
		name: 'Analytics',
		icon: BarChart3,
		children: [
			{
				id: 'analytics-revenue',
				name: 'Revenue Reports',
				icon: TrendingUp,
				href: '/analytics/revenue',
			},
			{
				id: 'analytics-trends',
				name: 'Trend Analysis',
				icon: TrendingUp,
				href: '/analytics/trends',
			},
			{
				id: 'analytics-users',
				name: 'User Reports',
				icon: Users,
				href: '/analytics/users',
			},
			{
				id: 'analytics-system',
				name: 'System Reports',
				icon: Server,
				href: '/analytics/system',
			},
		],
	},
	{
		id: 'reports',
		name: 'Reports',
		icon: FileText,
		children: [
			{
				id: 'reports-financial',
				name: 'Financial Reports',
				icon: BarChart3,
				href: '/reports/financial',
			},
			{
				id: 'reports-operational',
				name: 'Operational Reports',
				icon: Database,
				href: '/reports/operational',
			},
			{
				id: 'reports-custom',
				name: 'Custom Reports',
				icon: FileText,
				href: '/reports/custom',
			},
		],
	},
	// 5. ADMIN & CONFIGURATION (Less frequent)
	{
		id: 'ops-admin',
		name: 'Ops Admin',
		icon: Building2,
		children: [
			{
				id: 'clients',
				name: 'Clients',
				icon: Users,
				children: [
					{
						id: 'clients-add',
						name: 'New Client',
						icon: UserPlus,
						href: '/clients/add',
					},
					{
						id: 'clients-manage',
						name: 'Manage Clients',
						icon: Eye,
						href: '/clients/manage',
					},
					{
						id: 'clients-disable',
						name: 'Disable Clients',
						icon: UserX,
						href: '/clients/disable',
					},
				],
			},
			{
				id: 'sku-mapping',
				name: 'SKU Mapping',
				icon: Package,
				children: [
					{
						id: 'add-sku-mapping',
						name: 'New Mapping',
						icon: Link,
						href: '/ops-admin/map-sku/add',
					},
					{
						id: 'sku-listing',
						name: 'SKU Listing',
						icon: FileText,
						href: '/ops-admin/map-sku/listing',
					},
				],
			},
			{
				id: 'ops-admin-revenue',
				name: 'Revenue Setup',
				icon: TrendingUp,
				children: [
					{
						id: 'review-cost-type',
						name: 'Cost Types',
						icon: FileText,
						href: '/ops-admin/revenue/review-cost-type',
					},
					{
						id: 'review-category-type',
						name: 'Category Types',
						icon: FileText,
						href: '/ops-admin/revenue/review-category-type',
					},
				],
			},
			{
				id: 'ops-admin-QC',
				name: 'QC',
				icon: AlertCircle,
				children: [
					{
						id: 'QC-type',
						name: 'QC Type Listing',
						icon: FileText,
						href: '/ops-admin/qc/qc-type',
					},
				],
			},
			{
				id: 'ops-admin-client-escalations',
				name: 'Escalations',
				icon: AlertCircle,
				children: [
					{
						id: 'client-escalation-type',
						name: 'Escalation Type',
						icon: FileText,
						href: '/ops-admin/client-escalations/escalation-type',
					},
				],
			},
			{
				id: 'container-types',
				name: 'Container Types',
				icon: Package,
				children: [
					{
						id: 'container-listing',
						name: 'Container Listing',
						icon: FileText,
						href: '/ops-admin/containers/listing',
					},
				],
			},
			{
				id: 'facility-resources',
				name: 'Facility Resources',
				icon: Building2,
				href: '/ops-admin/facility-resources',
			},
			{
				id: 'vehicles',
				name: 'Vehicles',
				icon: Truck,
				children: [
					{
						id: 'vehicle-listing',
						name: 'Vehicle Listing',
						icon: FileText,
						href: '/ops-admin/vehicles/listing',
					},
				],
			},
			{
				id: 'users',
				name: 'Users',
				icon: Users,
				children: [
					{
						id: 'manage-users',
						name: 'Manage Users',
						icon: Users,
						href: '/ops-admin/users',
					},
					{
						id: 'add-user',
						name: 'Add User',
						icon: UserPlus,
						href: '/ops-admin/users/add',
					},
				],
			},
		],
	},
	{
		id: 'menu-management',
		name: 'Menu Management',
		icon: Menu,
		href: '/menu-management',
	},
	{
		id: 'sales',
		name: 'Sales',
		icon: ShoppingCart,
		children: [
			{
				id: 'leads',
				name: 'Leads',
				icon: UserPlus,
				href: '/sales/leads',
			},
			{
				id: 'my-leads',
				name: 'My Leads',
				icon: ClipboardList,
				href: '/leads/tracking',
			},
			{
				id: 'callbacks',
				name: "Today's Callbacks",
				icon: PhoneCall,
				href: '/leads/callbacks',
			},
			{
				id: 'lead-reports',
				name: 'Reports',
				icon: BarChart,
				href: '/leads/reports',
			},
		],
	},
	{
		id: 'hr',
		name: 'HR',
		icon: Users,
		children: [
			{
				id: 'hr-organization',
				name: 'Organization',
				icon: Building2,
				children: [
					{
						id: 'employees',
						name: 'Employees',
						icon: UserCog,
						href: '/hr/employees',
					},
					{
						id: 'departments',
						name: 'Departments',
						icon: Building,
						href: '/hr/departments',
					},
					{
						id: 'designations',
						name: 'Designations',
						icon: Award,
						href: '/hr/designations',
					},
					{
						id: 'salary-structures',
						name: 'Salary Structures',
						icon: BadgeDollarSign,
						href: '/hr/salary-structures',
					},
				],
			},
			{
				id: 'hr-recruitment',
				name: 'Recruitment',
				icon: Briefcase,
				children: [
					{
						id: 'job-posting',
						name: 'Job Posting',
						icon: Briefcase,
						href: '/hr/job-posting',
					},
					{
						id: 'recruitment-jobs',
						name: 'Job Openings',
						icon: Briefcase,
						href: '/recruitment/jobs',
					},
					{
						id: 'recruitment-add-candidate',
						name: 'Add Candidate',
						icon: UserPlus,
						href: '/recruitment/candidates/add',
					},
					{
						id: 'recruitment-applications',
						name: 'Applications',
						icon: KanbanSquare,
						href: '/recruitment/applications',
					},
					{
						id: 'recruitment-resume-search',
						name: 'Resume Search',
						icon: FileText,
						href: '/recruitment/resume-search',
					},
				],
			},
			{
				id: 'hr-self-service',
				name: 'My HR',
				icon: Calendar,
				children: [
					{
						id: 'my-holidays',
						name: 'My Holidays',
						icon: Calendar,
						href: '/hr/holidays',
					},
					{
						id: 'my-choices',
						name: 'My Choices',
						icon: ClipboardList,
						href: '/hr/holidays/my-choices',
					},
					{
						id: 'my-leaves',
						name: 'My Leaves',
						icon: FileText,
						href: '/hr/leaves',
					},
					{
						id: 'my-comp-off',
						name: 'My Comp-Off',
						icon: Calendar,
						href: '/hr/comp-off',
					},
					{
						id: 'my-attendance',
						name: 'My Attendance',
						icon: Activity,
						href: '/hr/attendance',
					},
					{
						id: 'attendance-regularize',
						name: 'Regularize',
						icon: ClipboardList,
						href: '/hr/attendance/regularize',
					},
				],
			},
			{
				id: 'hr-holiday-admin',
				name: 'HR Operations',
				icon: Database,
				children: [
					{
						id: 'team-hr',
						name: 'Team HR',
						icon: Users,
						children: [
							{
								id: 'team-choices',
								name: 'Team Choices',
								icon: Users,
								href: '/hr/holidays/team-choices',
							},
							{
								id: 'leave-requests',
								name: 'Leave Requests',
								icon: ClipboardList,
								href: '/hr/leave-requests',
							},
							{
								id: 'team-comp-off',
								name: 'Team Comp-Off',
								icon: Calendar,
								href: '/hr/team-comp-off',
							},
							{
								id: 'mark-attendance',
								name: 'Mark Attendance',
								icon: Activity,
								href: '/hr/attendance/mark',
							},
							{
								id: 'team-attendance',
								name: 'Team Attendance',
								icon: Users,
								href: '/hr/attendance/team',
							},
							{
								id: 'attendance-summary',
								name: 'Attendance Summary',
								icon: BarChart3,
								href: '/hr/attendance/summary',
							},
							{
								id: 'regularization-requests',
								name: 'Regularization Requests',
								icon: ClipboardList,
								href: '/hr/attendance/regularization-requests',
							},
						],
					},
					{
						id: 'hr-settings',
						name: 'HR Settings',
						icon: Database,
						children: [
							{
								id: 'holiday-master',
								name: 'Holiday Master',
								icon: Database,
								href: '/hr/holidays/master',
							},
							{
								id: 'choices-summary',
								name: 'Choices Summary',
								icon: BarChart,
								href: '/hr/holidays/summary',
							},
							{
								id: 'holiday-config',
								name: 'System Config',
								icon: UserCog,
								href: '/hr/holidays/config',
							},
							{
								id: 'leave-types',
								name: 'Leave Types',
								icon: FileText,
								href: '/hr/leave-types',
							},
							{
								id: 'leave-balances',
								name: 'Leave Balances',
								icon: BadgeDollarSign,
								href: '/hr/leave-balances',
							},
						],
					},
				],
			},
		],
	},
	{
		id: 'approvals',
		name: 'Approvals',
		icon: ShieldCheck,
		children: [
			{
				id: 'vendor-invoice-approvals',
				name: 'Vendor Invoice Approvals',
				icon: ShieldCheck,
				href: '/approvals/vendor-invoices',
			},
			// NOTE: the sidebar is server-permission gated (menu_permissions from login).
			// These three IDs must be whitelisted under approvals.children on the backend,
			// else filterMenusByPermissions() hides them. Until then they are URL-only.
			{
				id: 'email-payment-approvals',
				name: 'Payment Approvals',
				icon: Inbox,
				href: '/email-workflows',
			},
			{
				// approver/finance surface (menu seed: user types 2/17/30/31)
				id: 'email-payment-request-approvals',
				name: 'Payment Request Approvals',
				icon: ShieldCheck,
				href: '/approvals/payment-requests',
			},
			{
				id: 'email-raise-request',
				name: 'Raise Request',
				icon: FileText,
				href: '/email-workflows/new',
			},
			{
				id: 'email-approval-reports',
				name: 'Approval Reports',
				icon: BarChart3,
				href: '/email-workflows/reports',
			},
		],
	},
	{
		id: 'invoice',
		name: 'Invoice',
		icon: FileText,
		children: [
			{
				id: 'invoice-list',
				name: 'Invoice Listing',
				icon: FileText,
				href: '/billing/invoice',
			},
			{
				id: 'billing-details',
				name: 'Billing Details',
				icon: FileText,
				href: '/billing/details',
			},
			{
				// Billing redesign alpha — handoff/BILLING_REDESIGN_PLAN.md
				id: 'pulse',
				name: 'Pulse (new)',
				icon: Zap,
				href: '/billing/pulse',
			},
			{
				id: 'clients-ledger',
				name: 'Clients (new)',
				icon: Users,
				href: '/billing/clients',
			},
			{
				id: 'follow-up-tracker',
				name: 'Follow-Up Tracker',
				icon: Inbox,
				href: '/billing/follow-up-tracker',
			},
			{
				id: 'customer-intelligence',
				name: 'Customer Intelligence',
				icon: Users,
				href: '/billing/customer-intelligence',
			},
			// Legacy menu items — kept here (commented) for reference + easy
			// rollback. Their hrefs are now ledger-filter / pulse shortcuts so
			// the URLs still work via Navigate redirects in routes.tsx, but
			// they don't render in the sidebar. Pulse + Clients (new) cover
			// every entry below. Re-enable any line if a user explicitly
			// asks for the standalone view back.
			/*
			{
				id: 'client-health',
				name: 'Client Health',
				icon: Activity,
				href: '/billing/clients',
			},
			{
				id: 'overdue-behaviour',
				name: 'Overdue + Behaviour',
				icon: TrendingUp,
				href: '/billing/clients?filter=disputing',
			},
			{
				id: 'pipeline-gaps',
				name: 'Pipeline Blindspot',
				icon: Eye,
				href: '/billing/clients?filter=untracked',
			},
			{
				id: 'broken-commitments',
				name: 'Broken Commitments',
				icon: AlertCircle,
				href: '/billing/clients?filter=broken',
			},
			{
				id: 'ceo-overview',
				name: 'CEO Overview',
				icon: BarChart3,
				href: '/billing/pulse',
			},
			*/
		],
	},
	{
		id: 'task-board',
		name: 'Task Board',
		icon: KanbanSquare,
		href: '/task-board',
	},
	{
		id: 'help',
		name: 'Help & Support',
		icon: HelpCircle,
		href: '/help',
	},
];

// Helper function to find menu item by ID
export const findMenuItemById = (
	id: string,
	menus: MenuItem[] = ALL_MENU_ITEMS
): MenuItem | null => {
	for (const menu of menus) {
		if (menu.id === id) {
			return menu;
		}
		if (menu.children) {
			const found = findMenuItemById(id, menu.children);
			if (found) return found;
		}
	}
	return null;
};

// Helper function to get all menu IDs (for debugging)
export const getAllMenuIds = (menus: MenuItem[] = ALL_MENU_ITEMS): string[] => {
	const ids: string[] = [];

	for (const menu of menus) {
		ids.push(menu.id);
		if (menu.children) {
			ids.push(...getAllMenuIds(menu.children));
		}
	}

	return ids;
};
